import { useState } from "react";
import { motion } from "framer-motion";
import { Eye, Cpu, Gauge } from "lucide-react";
const STEPS = [
  {
    number: "01",
    tag: "Analysis & Discovery",
    title: "Understand Your Vision",
    icon: Eye,
    description:
      "We start by listening to you. Our team dives deep into understanding your business goals, challenges, and unique requirements. By aligning AI solutions with your vision, we ensure that every strategy is tailored to achieve meaningful outcomes for you.",
    points: ["Process & pain-point audit", "Strategic feasibility alignment"],
  },
  {
    number: "02",
    tag: "Orchestration & AI",
    title: "Design Intelligent Solutions",
    icon: Cpu,
    description:
      "We design and implement AI solutions that seamlessly integrate into your processes. Whether it’s optimizing operations, automating workflows, or uncovering new opportunities, we build strategies that deliver measurable results and long-term value.",
    points: ["Autonomous workflow design", "Low-code system orchestration"],
  },
  {
    number: "03",
    tag: "Continuous Governance",
    title: "Support and Optimization",
    icon: Gauge,
    description:
      "Success with AI doesn’t stop at implementation. We monitor, refine, and adapt the solutions we create to keep pace with your evolving needs. Our commitment is to ensure that you stay ahead of the curve and maximize the benefits.",
    points: ["Proactive telemetry monitoring", "Adaptive accuracy fine-tuning"],
  },
];

export default function StepsSection() {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  return (
    <section className="relative w-full lg:py-24 md:py-16 py-10 bg-[#000036] overflow-hidden">
      <div className="mx-auto 3xl:w-4/5 lg:w-11/12 px-6 lg:px-12 relative z-10">
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-20">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full  backdrop-blur-md mb-4">
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-white">
              Steps to Success
            </span>
          </div>

          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white">
            How we help your{" "}
            <span className="italic font-serif font-normal text-[#62d4f7]">
              Business
            </span>{" "}
            Succeed
          </h2>

          <p className="mt-5 text-base sm:text-lg text-white leading-relaxed max-w-2xl">
            We bridge the gap between value-focused technology, risk, and
            advisory services.
          </p>
        </div>

        <div className="rounded-xl border border-black/10 bg-white backdrop-blur-2xl shadow-[0_12px_45px_rgba(0,0,0,0.035)] overflow-hidden">
          <div className="lg:grid hidden grid-cols-3 border-b border-slate-200/80 bg-slate-50/50">
            {STEPS.map((step, idx) => (
              <div
                key={step.number}
                className={`relative px-6 py-4 flex items-center  md:block justify-between transition-colors duration-300 ${
                  idx !== 2 ? "border-r border-slate-200/80" : ""
                } ${hoveredIndex === idx ? "bg-white" : ""}`}
              >
                <span className=" text-xs  font-bold tracking-widest text-slate-400">
                  STEP // 0{idx + 1}
                </span>

                <span
                  className={`h-2 w-2 rounded-full transition-all duration-300 ${
                    hoveredIndex === idx
                      ? "bg-slate-950 scale-125"
                      : "bg-slate-300"
                  }`}
                />

                {hoveredIndex === idx && (
                  <motion.div
                    layoutId="monolithIndicator"
                    className="absolute top-0 inset-x-0 h-[2.5px] bg-[#000036]"
                    transition={{ type: "spring", stiffness: 350, damping: 30 }}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-slate-200/80">
            {STEPS.map((step, idx) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.number}
                  onMouseEnter={() => setHoveredIndex(idx)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  className="group relative p-6 sm:p-10 flex flex-col justify-between transition-all duration-300 hover:bg-white/90"
                >
                  <div>
                    <div className="flex items-start justify-between mb-8">
                      <span className="font-serif text-7xl sm:text-8xl font-light tracking-tighter text-[#000036] transition-all duration-500 group-hover:text-black group-hover:-translate-y-1">
                        {step.number}
                      </span>

                      <div className="h-14 w-14 rounded-2xl bg-white border border-black/5 shadow-xs flex items-center justify-center text-slate-800 transition-all duration-300 group-hover:bg-[#000036] group-hover:text-white group-hover:rotate-[-6deg]">
                        <Icon size={30} strokeWidth={1.75} />
                      </div>
                    </div>
                    <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 block mb-2">
                      {step.tag}
                    </span>
                    <h3 className="text-2xl font-bold tracking-tight text-[#000036] leading-snug mb-4">
                      {step.title}
                    </h3>
                    <p className="text-base leading-relaxed text-slate-600 font-normal mb-8">
                      {step.description}
                    </p>
                  </div>
                  <div className="pt-3 border-t border-slate-100/90 flex flex-col gap-2"></div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
